import { useCallback } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import { currencyFormatter, toLocaleStringNumber } from 'utils/functions';

import ActionButton from './ActionButton';

interface CallProps {
  disabled: boolean;
  /** See PokerActions' submitAction - carries the re-send closure and the action name. */
  submitAction: (send: (stamp: string) => void, name: string) => void;
  /** The emit was not acked in time (or could not be sent). PokerActions decides what happens. */
  onAckTimeout: () => void;
  triggerHideServerTimeout: () => void;
}

const CallAction = ({
  disabled,
  submitAction,
  onAckTimeout,
  triggerHideServerTimeout
}: CallProps) => {
  const { call, allIn } = useGameService();
  const tableType = useTableSelector((table) => table.game.tabletype);

  const { playersStack } = useTableSelector((table) => table.gameState.pokerActions.betMultiplier);

  const { allIn: isAllIn, playerCallAmount: callAmount } = useTableSelector(
    (table) => table.gameState.pokerActions
  );

  const onInternalClick = useCallback(() => {
    if (isAllIn) {
      submitAction((stamp) => allIn(stamp, playersStack, triggerHideServerTimeout, onAckTimeout), 'allin');
    } else {
      submitAction((stamp) => call(stamp, triggerHideServerTimeout, onAckTimeout), 'call');
    }
    // Every captured value belongs here. `call` and `allIn` come from
    // useGameService and are useCallback'd on [baseParams, socket], so their
    // identity CHANGES when the socket or the table params change - and
    // submitAction stores this closure in pendingRef and re-invokes it on every
    // retry. Omitting them let a committed action, and each of its re-sends,
    // keep targeting the service bound to the socket that existed at click time
    // (PR review). `triggerHideServerTimeout` is the ack path's success
    // callback, so a stale one would hide the wrong turn's banner.
  }, [
    isAllIn,
    playersStack,
    submitAction,
    onAckTimeout,
    allIn,
    call,
    triggerHideServerTimeout
  ]);

  const label = isAllIn
    ? `All In ${
        tableType === 'm' || tableType === 't'
          ? toLocaleStringNumber(playersStack)
          : currencyFormatter(playersStack)
      }`
    : `Call ${
        tableType === 'm' || tableType === 't'
          ? toLocaleStringNumber(callAmount)
          : currencyFormatter(callAmount)
      }`;

  return (
    <ActionButton
      variant={isAllIn ? 'allin' : 'call'}
      icon={isAllIn ? 'allin' : 'call'}
      onClick={onInternalClick}
      disabled={disabled}>
      {label}
    </ActionButton>
  );
};

export default CallAction;
